import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import * as agentChatService from "@/stores/agentChatService";
import { useUserPurchases } from "@/hooks/useUserPurchases";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { NotificationBell } from "./NotificationBell";
import {
  // Nav items
  Gauge,           // Dashboard
  Crosshair,       // Execution Terminal
  NotebookPen,     // Trade Journal
  BarChart3,       // Analytics
  MonitorDot,      // Command Center
  Brain,           // Agents
  Trophy,          // Leaderboard
  Flame,           // Edge Score
  ShieldCheck,     // Prop Challenge
  History,         // Trade Replay
  Receipt,         // Tax / MTD
  Library,         // Strategy Library
  // Bottom bar
  Crown,           // Elite Plan
  Zap,             // AI Credits
  Settings2,       // Settings
  Terminal,        // Admin
  Building2,       // Firm Admin
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

//  Nav Config 

const navItems = [
  { icon: Gauge,       label: "Dashboard",          path: "/",                  exact: true,  sectionLabel: null },
  { icon: Crosshair,   label: "Execution Terminal", path: "/execution-terminal", exact: false, sectionLabel: null },
  { icon: NotebookPen, label: "Trade Journal",      path: "/journal",           exact: false, sectionLabel: null },
  { icon: BarChart3,   label: "Analytics",          path: "/analytics",         exact: false, sectionLabel: null },
  { icon: MonitorDot,  label: "Command Center",     path: "/command-center",    exact: false, sectionLabel: null },
  { icon: Brain,       label: "Agents",             path: "/agents?agent=journal", exact: false, sectionLabel: null },
  { icon: Trophy,      label: "Leaderboard",        path: "/leaderboard",       exact: false, sectionLabel: null },
  { icon: Flame,       label: "Edge Score",         path: "/edge-score",        exact: false, sectionLabel: null },
  { icon: ShieldCheck, label: "Prop Challenge",     path: "/prop-challenge",    exact: false, sectionLabel: null },
  { icon: History,     label: "Trade Replay",       path: "/trade-replay",      exact: false, sectionLabel: null },
  { icon: Receipt,     label: "Tax / MTD",          path: "/tax",               exact: false, sectionLabel: null },
  // Strategy Library temporarily hidden for launch (see LEGAL_AUDIT.md). Restore this line + MARKETPLACE_ENABLED in ShopPage.tsx to bring it back.
  // { icon: Library,     label: "Strategy Library",   path: "/strategy-library",  exact: false, sectionLabel: null },
];

//  Context 

interface SidebarContextType {
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
  mobileOpen: false,
  setMobileOpen: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <SidebarContext.Provider value={{ mobileOpen, setMobileOpen }}>
      {children}
    </SidebarContext.Provider>
  );
}

//  Mobile Header 

export function MobileHeader() {
  const { setMobileOpen } = useSidebar();
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-black border-b border-white/[0.06] flex items-center justify-between px-4">
      <div className="flex items-center gap-2.5">
        <svg width="24" height="17" viewBox="0 0 28 20" fill="none">
          <polyline
            points="2,17 7,3 13,12 19,3 25,10"
            stroke="#1d9e75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
          />
          <circle cx="2" cy="17" r="2" fill="#1d9e75"/>
          <circle cx="25" cy="10" r="2" fill="#1d9e75"/>
        </svg>
        <span className="font-mono text-[10px] tracking-[0.15em] text-white/50 uppercase">Margin Metrics</span>
      </div>
      <div className="flex items-center gap-1">
        <NotificationBell isMobile />
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 hover:bg-white/5 transition-colors duration-150"
        >
          <Menu className="w-5 h-5" style={{ color: "rgba(255,255,255,0.5)" }} strokeWidth={1.5} />
        </button>
      </div>
    </header>
  );
}

//  Nav item 

function NavItem({
  icon: Icon,
  label,
  path,
  exact,
  isMobile,
  expanded,
  onNavigate,
  showDot,
}: {
  icon: React.ElementType;
  label: string;
  path: string;
  exact: boolean;
  isMobile: boolean;
  expanded: boolean;
  onNavigate: () => void;
  showDot?: boolean;
}) {
  return (
    <NavLink
      to={path}
      end={exact}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group/item relative flex items-center h-10 px-3 gap-3 transition-colors duration-150",
          !isActive && "hover:bg-white/[0.05]"
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: "var(--brand-primary, #1d9e75)" }} />
          )}
          <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon
              size={16}
              strokeWidth={1.5}
              className={cn(
                "shrink-0 transition-colors duration-150",
                isActive
                  ? "text-white"
                  : "text-white/35 group-hover/item:text-white/70"
              )}
            />
            {showDot && (
              <span style={{ width: 5, height: 5, background: "#1d9e75", position: "absolute", top: 0, right: 0 }} />
            )}
          </span>
          <span
            className={cn(
              "text-[11px] font-medium uppercase tracking-[0.08em] whitespace-nowrap transition-colors duration-150 pointer-events-none",
              isActive
                ? "text-white"
                : "text-white/35 group-hover/item:text-white/70",
              !isMobile && (expanded ? "opacity-100" : "opacity-0")
            )}
          >
            {label}
          </span>
        </>
      )}
    </NavLink>
  );
}

//  Sidebar Component 

export function Sidebar() {
  const { mobileOpen, setMobileOpen } = useSidebar();
  const { user, signOut, subscription } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const { aiCredits } = useUserPurchases();
  const navigate = useNavigate();

  const { data: userRole } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user.id);
      if (!data || data.length === 0) return "user";
      if (data.some((r) => r.role === "admin")) return "admin";
      if (data.some((r) => r.role === "moderator")) return "moderator";
      return "user";
    },
    enabled: !!user?.id,
  });

  const isAdmin = userRole === "admin";

  const { data: isFirmAdmin } = useQuery({
    queryKey: ["is-firm-admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase
        .from("tenant_users")
        .select("id")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .limit(1)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user?.id,
  });

  const [agentUnread, setAgentUnread] = useState(() => agentChatService.getState().unreadCount);
  useEffect(() => agentChatService.subscribe(() => {
    setAgentUnread(agentChatService.getState().unreadCount);
  }), []);

  const userInitials = user?.email?.slice(0, 2).toUpperCase() || "??";
  const userEmail = user?.email || "";

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch {
      toast.error("Sign out failed");
    }
  };

  const handleNavigate = () => setMobileOpen(false);

  //  Shared inner content 
  const sidebarInner = (isMobile: boolean) => (
    <div className="flex flex-col h-full">

      {/* Logo */}
      <div
        className={cn(
          "flex items-center h-14 px-3 border-b border-white/[0.06] shrink-0 gap-3",
          isMobile && "justify-between"
        )}
      >
        <svg width="24" height="17" viewBox="0 0 28 20" fill="none" className="shrink-0">
          <polyline
            points="2,17 7,3 13,12 19,3 25,10"
            stroke="#1d9e75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
          />
          <circle cx="2" cy="17" r="2" fill="#1d9e75" />
          <circle cx="25" cy="10" r="2" fill="#1d9e75" />
        </svg>
        <div
          className={cn(
            "overflow-hidden",
            !isMobile && cn("transition-opacity duration-150", expanded ? "opacity-100" : "opacity-0")
          )}
        >
          <div className="font-mono text-[10px] font-bold tracking-[0.15em] text-white/60 uppercase leading-tight whitespace-nowrap">
            Margin
          </div>
          <div className="font-mono text-[10px] font-bold tracking-[0.15em] text-white/60 uppercase leading-tight whitespace-nowrap">
            Metrics
          </div>
        </div>
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto p-1.5 hover:bg-white/5 transition-colors duration-150"
          >
            <X className="w-4 h-4" style={{ color: "rgba(255,255,255,0.4)" }} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {/* Main nav */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {navItems.map((item) => (
          <div key={item.path}>
            {item.sectionLabel && (isMobile || expanded) && (
              <div
                style={{
                  padding: "8px 12px 2px",
                  fontFamily: "monospace",
                  fontSize: 8,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.18)",
                }}
              >
                {item.sectionLabel}
              </div>
            )}
            <NavItem
              {...item}
              isMobile={isMobile}
              expanded={expanded}
              onNavigate={handleNavigate}
              showDot={item.path.startsWith("/agents") && agentUnread > 0}
            />
          </div>
        ))}

        {/* Admin (conditional) */}
        {isAdmin && (
          <NavLink
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                "group/item relative flex items-center h-10 px-3 gap-3 transition-colors duration-150",
                !isActive && "hover:bg-white/[0.05]"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: "var(--brand-primary, #1d9e75)" }} />
                )}
                <Terminal
                  size={16}
                  strokeWidth={1.5}
                  className={cn(
                    "shrink-0 transition-colors duration-150",
                    isActive
                      ? "text-white"
                      : "text-white/35 group-hover/item:text-white/70"
                  )}
                />
                <span
                  className={cn(
                    "text-[11px] font-medium uppercase tracking-[0.08em] whitespace-nowrap transition-colors duration-150",
                    isActive
                      ? "text-white"
                      : "text-white/35 group-hover/item:text-white/70",
                    !isMobile && (expanded ? "opacity-100" : "opacity-0")
                  )}
                >
                  Admin
                </span>
              </>
            )}
          </NavLink>
        )}

        {/* Firm Admin (conditional) */}
        {isFirmAdmin && !isAdmin && (
          <NavLink
            to="/firm-admin"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                "group/item relative flex items-center h-10 px-3 gap-3 transition-colors duration-150",
                !isActive && "hover:bg-white/[0.05]"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: "var(--brand-primary, #1d9e75)" }} />
                )}
                <Building2
                  size={16}
                  strokeWidth={1.5}
                  className={cn(
                    "shrink-0 transition-colors duration-150",
                    isActive
                      ? "text-white"
                      : "text-white/35 group-hover/item:text-white/70"
                  )}
                />
                <span
                  className={cn(
                    "text-[11px] font-medium uppercase tracking-[0.08em] whitespace-nowrap transition-colors duration-150",
                    isActive
                      ? "text-white"
                      : "text-white/35 group-hover/item:text-white/70",
                    !isMobile && (expanded ? "opacity-100" : "opacity-0")
                  )}
                >
                  Firm Admin
                </span>
              </>
            )}
          </NavLink>
        )}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-white/[0.06] shrink-0">

        {/* Plan badge */}
        <div className="flex items-center h-9 px-3 gap-3 overflow-hidden">
          <Crown size={16} strokeWidth={1.5} className="shrink-0 text-[#1d9e75]" />
          <span
            className={cn(
              "text-[10px] font-bold uppercase tracking-[0.1em] text-[#1d9e75] whitespace-nowrap transition-opacity duration-100",
              !isMobile && (expanded ? "opacity-100" : "opacity-0")
            )}
          >
            {subscription.tier === "free" ? "Free Tier" : `${subscription.tier} Plan`}
          </span>
        </div>

        {/* AI Credits */}
        <div className="flex items-center h-9 px-3 gap-3 overflow-hidden">
          <Zap size={16} strokeWidth={1.5} className="shrink-0" style={{ color: "rgba(255,255,255,0.35)" }} />
          <span
            className={cn(
              "text-[11px] whitespace-nowrap transition-opacity duration-100",
              !isMobile && (expanded ? "opacity-100" : "opacity-0")
            )}
            style={{ color: "rgba(255,255,255,0.35)" }}
          >
            Credits{" "}
            <span className="font-mono font-bold text-white">{aiCredits}</span>
          </span>
        </div>

        {/* Notification Bell row — full row is clickable */}
        <NotificationBell rowMode labelVisible={isMobile || expanded} />

        {/* Settings */}
        <NavLink
          to="/settings"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            cn(
              "group/item relative flex items-center h-9 px-3 gap-3 transition-colors duration-150 overflow-hidden",
              !isActive && "hover:bg-white/[0.05]"
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: "var(--brand-primary, #1d9e75)" }} />
              )}
              <Settings2
                size={16}
                strokeWidth={1.5}
                className={cn(
                  "shrink-0 transition-colors duration-150",
                  isActive
                    ? "text-white"
                    : "text-white/35 group-hover/item:text-white/70"
                )}
              />
              <span
                className={cn(
                  "text-[11px] font-medium uppercase tracking-[0.08em] whitespace-nowrap transition-colors duration-150",
                  isActive
                    ? "text-white"
                    : "text-white/35 group-hover/item:text-white/70",
                  !isMobile && (expanded ? "opacity-100" : "opacity-0")
                )}
              >
                Settings
              </span>
            </>
          )}
        </NavLink>

        {/* User profile + sign out */}
        <div className="flex items-center h-10 px-3 gap-3 overflow-hidden border-t border-white/[0.04]">
          <div className="w-4 h-4 shrink-0 flex items-center justify-center border border-[#1d9e75]/40 bg-[#1d9e75]/10">
            <span className="text-[7px] font-bold" style={{ color: "#1d9e75" }}>
              {userInitials}
            </span>
          </div>
          <div
            className={cn(
              "flex flex-1 items-center justify-between min-w-0 gap-2 transition-opacity duration-100",
              !isMobile && (expanded ? "opacity-100" : "opacity-0")
            )}
          >
            <span className="text-[10px] truncate" style={{ color: "rgba(255,255,255,0.35)" }}>
              {userEmail}
            </span>
            <button
              onClick={handleSignOut}
              className="p-1 hover:bg-white/10 transition-colors duration-150 shrink-0"
              title="Sign out"
            >
              <LogOut size={14} strokeWidth={1.5} style={{ color: "rgba(255,255,255,0.35)" }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop: fixed overlay -- expands on hover without shifting page content */}
      <aside
        className={cn(
          "hidden lg:flex fixed top-0 left-0 h-screen flex-col z-50",
          "bg-black border-r border-white/[0.06]",
          "transition-[width] duration-200 ease-out",
          expanded ? "w-[180px]" : "w-10"
        )}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        {sidebarInner(false)}
      </aside>
      {/* Spacer so page content doesn't sit under the fixed sidebar */}
      <div className="hidden lg:block flex-shrink-0 w-10" />

      {/* Mobile: overlay drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-60 bg-black border-r border-white/[0.06] flex flex-col animate-slide-in-left">
            {sidebarInner(true)}
          </aside>
        </div>
      )}
    </>
  );
}
